import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { CallState, LoadingState } from '@lcs/shared/api';
import { TileLayoutConfigurationReportParameters } from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterFormFactoryService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form-factory.service';
import { ReportParameterFormService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { Store } from '@ngrx/store';
import { Tiles, TileTypes, TileViewModes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TileParametersBase } from 'src/app/pages/tiles/models/tile-parameters.model';
import { getTileViewMode } from 'src/app/pages/tiles/models/tile-view-mode.model';
import { TileState } from 'src/app/pages/tiles/state/tile.reducer';
import { getTile } from 'src/app/pages/tiles/state/tiles.selectors';
import { NgChanges } from 'src/lib/ng-changes';
import { DashboardTile, PreviewDashboardTile, VerboseTileIds } from '../../../models/dashboard-tile.model';
import { Tile } from '../../../models/tile.model';
import { TileSet, TileSetType } from '../../../tile-set-type-select/models/tile-set.model';
import { loadPreviewTile } from '../../state/add-dashboard-tile.actions';

@Component({
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-dashboard-tile-form',
    templateUrl: './add-dashboard-tile-form.component.html',
    styleUrls: ['./add-dashboard-tile-form.component.scss'],
    host: {
        class: 'add-dashboard-tile-form-wrapper',
    },
    standalone: false
})
export class AddDashboardTileFormComponent<P extends TileParametersBase> implements OnInit, OnDestroy, OnChanges {
   @Input('tile') tile: Tile | null;

   @Input('tileSet') tileSet: TileSet | null;

   @Input() callState: CallState;

   @Output() back = new EventEmitter<void>();

   @Output() submit = new EventEmitter<{
      previewTileConfig: PreviewDashboardTile | null;
      formService: ReportParameterFormService<P>;
   }>();

   @Output() tileSetTypeChange = new EventEmitter<TileSetType>();

   reportParameterFormService: ReportParameterFormService<P> | null = null;

   previewTileConfig: PreviewDashboardTile | null = null;

   previewTileData: TileState | undefined;

   get isLoading(): boolean {
      return this.callState === LoadingState.Loading;
   }

   private customTiles: DashboardTile[] = [];

   private destroyed$ = new Subject<void>();

   private previousColorValue: string = '#2b376b';

   private previousNameValue: string | null = null;

   private previousRefreshTimeValue: string | null = null;

   constructor(
      private reportParameterFormServiceFactory: ReportParameterFormFactoryService,
      private store: Store,
      private cd: ChangeDetectorRef
   ) {}
   ngOnInit(): void {
      this.store
         .select(getTile(VerboseTileIds.previewTile))
         .pipe(takeUntil(this.destroyed$))
         .subscribe((ptd) => {
            this.previewTileData = ptd;
            this.cd.detectChanges();
         });
      // this.store
      //    .select(getCustomTiles)
      //    .pipe(takeUntil(this.destroyed$))
      //    .subscribe((customTiles) => {
      //       this.customTiles = customTiles ?? [];
      //    });

      if (this.reportParameterFormService) {
         const formValues = this.reportParameterFormService?.form?.value?.values;

         const currentNameControl = formValues.find(
            (value: { parameterType: TileLayoutConfigurationReportParameters }) =>
               value.parameterType === TileLayoutConfigurationReportParameters.Name
         );

         this.previousNameValue = currentNameControl.name;
      }
   }

   ngOnChanges(changes: NgChanges<AddDashboardTileFormComponent<P>>): void {
      if (changes.tile) {
         this.reinitializeFormService();
      }
   }

   ngOnDestroy(): void {
      this.reportParameterFormService = null;
      this.destroyed$.next();
   }

   onBack() {
      this.reportParameterFormService = null;
      this.back.emit();
   }

   onSubmit() {
      this.reportParameterFormService?.form.markAllAsTouched();
      this.reportParameterFormService?.form.updateValueAndValidity();

      const currentPreviewTileConfig = this.getPreviewTileConfig();
      if (currentPreviewTileConfig && this.reportParameterFormService?.form.valid && !this.isLoading) {
         this.submit.emit({
            previewTileConfig: currentPreviewTileConfig
               ? {
                    ...currentPreviewTileConfig,
                    dashboardTileLayoutConfiguration: {
                       ...currentPreviewTileConfig.dashboardTileLayoutConfiguration,
                       ...this.getTileAddSize(currentPreviewTileConfig),
                    },
                 }
               : null,
            formService: this.reportParameterFormService,
         });
      }
   }

   onReportParametersFormChange() {
      const formValues = this.reportParameterFormService?.form?.value?.values ?? [];
      const currentColorControl = formValues.find(
         (value: { parameterType: TileLayoutConfigurationReportParameters }) =>
            value.parameterType === TileLayoutConfigurationReportParameters.Color
      );
      const currentNameControl = formValues.find(
         (value: { parameterType: TileLayoutConfigurationReportParameters }) =>
            value.parameterType === TileLayoutConfigurationReportParameters.Name
      );
      const currentRefreshTimeControl = formValues.find(
         (value: { parameterType: TileLayoutConfigurationReportParameters }) =>
            value.parameterType === TileLayoutConfigurationReportParameters.RefreshTimeSeconds
      );

      if (
         currentColorControl?.color === this.previousColorValue &&
         currentNameControl?.name === this.previousNameValue &&
         currentRefreshTimeControl?.refreshTimeSeconds === this.previousRefreshTimeValue
      ) {
         this.setPreviewTileConfig();
      }

      if (this.previousColorValue !== currentColorControl?.color) {
         if (this.previewTileConfig?.dashboardTileLayoutConfiguration) {
            this.previewTileConfig = {
               ...this.previewTileConfig,
               dashboardTileLayoutConfiguration: {
                  ...this.previewTileConfig.dashboardTileLayoutConfiguration,
                  color: currentColorControl?.color,
               },
            };
         }
         this.cd.detectChanges();
      }
      this.previousColorValue = currentColorControl?.color;
      this.previousNameValue = currentNameControl?.name;
      this.previousRefreshTimeValue = currentRefreshTimeControl?.refreshTimeSeconds;
   }

   onTileSetTypeChange(tileSetType: TileSetType) {
      this.tileSetTypeChange.emit(tileSetType);
   }

   onTileLayoutConfigurationsInitialized() {
      const valuesFormArray = this.reportParameterFormService?.form.controls.values as UntypedFormArray;
      const valuesControls = valuesFormArray.controls;
      const nameControl = (
         valuesControls.find(
            (fg) => fg.value.parameterType === TileLayoutConfigurationReportParameters.Name
         ) as UntypedFormGroup
      ).controls.name;

      let name = this.tileSet?.description;
      if (this.tile?.isCustom) {
         let i = 1;
         while (this.customTiles.some((ct) => ct.dashboardTileLayoutConfiguration.name === `Custom ${i}`)) {
            i++;
         }
         name = `Custom ${i}`;
      }
      nameControl?.setValue(name);
   }

   private getTileAddSize(dashboardTile: PreviewDashboardTile): { height: number; width: number } {
      const typeSizeMap: { [key in TileTypes]?: { height: number; width: number } } = {
         [TileTypes.BarGraph]: { height: 2, width: 3 },
         [TileTypes.LineGraph]: { height: 2, width: 3 },
         [TileTypes.PieGraph]: { height: 2, width: 2 },
         [TileTypes.PieGridGraph]: { height: 2, width: 3 },
      };

      const tileSizeMap: { [key in Tiles]?: { height: number; width: number } } = {
         [Tiles.Clock]: { height: 1, width: 2 },
         [Tiles.CallParking]: { height: 1, width: 2 },
         [Tiles.QueueMembers]: { height: 2, width: 5 },
         [Tiles.PhoneQueues]: { height: 2, width: 5 },
         [Tiles.NDTWaitTimesGraph]: { height: 2, width: 5 },
      };

      // Properties in the tileSizeMap should take priority over the typeSizeMap
      return (
         tileSizeMap[dashboardTile.tile.tileId] ??
         typeSizeMap[dashboardTile.tile.typeId ?? ''] ?? { height: 1, width: 1 }
      );
   }

   private reinitializeFormService() {
      if (!this.tile) return;

      let dirtyControls: AbstractControl[] = [];
      if (this.reportParameterFormService) {
         dirtyControls = (this.reportParameterFormService.form.controls.values as UntypedFormArray).controls?.filter(
            (c) => c.dirty
         );
      }

      this.reportParameterFormService = this.reportParameterFormServiceFactory.getService(this.tile.tileId);

      dirtyControls?.forEach((dc) => {
         const match = (this.reportParameterFormService?.form.controls.values as UntypedFormArray).controls.find(
            (vc) => vc.value.parameterType === dc.value.parameterType
         );
         match?.setValue(dc.value, { emitEvent: false });
         match?.markAsDirty();
      });

      this.setPreviewTileConfig();
   }

   private getPreviewTileConfig(): PreviewDashboardTile | null {
      if (!(this.reportParameterFormService && this.tile)) return null;

      const parameters: P | null = this.reportParameterFormService.getValue(true);
      if (parameters) {
         const config: PreviewDashboardTile = {
            tile: this.tile,
            dashboardTileId: VerboseTileIds.previewTile,
            totalMillisecondsProcessed: 0,
            totalTimesProcessed: 0,
            dashboardTileLayoutConfiguration: {
               name: parameters.tileLayoutConfiguration.name,
               color: parameters.tileLayoutConfiguration.color,
               refreshTimeSeconds: parameters.tileLayoutConfiguration.refreshTimeSeconds,
               width: Infinity,
               height: Infinity,
            },
            viewMode: TileViewModes.Medium,
            dashboardTileParameters: Object.keys(parameters)
               .map((k) => parameters[k])
               .filter((p) => p.parameterType !== undefined),
         };
         return {
            ...config,
            viewMode: getTileViewMode(config, true),
         };
      }
      return null;
   }

   private setPreviewTileConfig() {
      this.previewTileConfig = this.getPreviewTileConfig();
      if (this.previewTileConfig) this.store.dispatch(loadPreviewTile({ previewTileConfig: this.previewTileConfig }));
   }
}
